/**
 * Centralized error mapping for the platform
 * Maps technical error codes and messages to user-friendly Portuguese messages
 */

export interface AppError {
    code: string;
    message: string;
    title: string;
    severity: 'error' | 'warning' | 'info';
}

export const PLATFORM_ERRORS: Record<string, AppError> = {
    // Connection Errors
    'ERR_CONNECTION_REFUSED': {
        code: 'CONNECTION_REFUSED',
        title: 'Erro de Conexão',
        message: 'Não foi possível conectar ao servidor. O sistema pode estar em manutenção ou o serviço está temporariamente indisponível.',
        severity: 'error',
    },
    'FAILED_TO_FETCH': {
        code: 'FAILED_TO_FETCH',
        title: 'Erro de Rede',
        message: 'Falha na comunicação com o servidor. Verifique sua conexão com a internet e tente novamente.',
        severity: 'error',
    },

    // Authentication Errors
    'UNAUTHORIZED': {
        code: 'UNAUTHORIZED',
        title: 'Acesso Negado',
        message: 'Sua sessão expirou ou você não tem permissão para acessar este recurso. Por favor, faça login novamente.',
        severity: 'warning',
    },
    'FORBIDDEN': {
        code: 'FORBIDDEN',
        title: 'Acesso Proibido',
        message: 'Você não tem permissão para realizar esta ação.',
        severity: 'error',
    },

    // Rate Limiting
    'RATE_LIMIT_EXCEEDED': {
        code: 'RATE_LIMIT_EXCEEDED',
        title: 'Muitas Requisições',
        message: 'Você atingiu o limite de requisições. Por favor, aguarde um momento antes de tentar novamente.',
        severity: 'warning',
    },

    // Storage & Files
    'FILE_TOO_LARGE': {
        code: 'FILE_TOO_LARGE',
        title: 'Arquivo muito grande',
        message: 'O arquivo enviado excede o limite de tamanho permitido.',
        severity: 'warning',
    },
    'UNSUPPORTED_FILE_TYPE': {
        code: 'UNSUPPORTED_FILE_TYPE',
        title: 'Tipo de arquivo não suportado',
        message: 'O formato do arquivo enviado não é suportado pelo sistema.',
        severity: 'warning',
    },

    // Default Error
    'UNKNOWN_ERROR': {
        code: 'UNKNOWN_ERROR',
        title: 'Erro Inesperado',
        message: 'Ocorreu um erro inesperado. Nossa equipe técnica já foi notificada.',
        severity: 'error',
    }
};

/**
 * Maps a technical error message or code to a user-friendly AppError object
 */
export const mapPlatformError = (error: any): AppError => {
    const errorMessage = error?.message || String(error);

    if (errorMessage.includes('Failed to fetch')) {
        return PLATFORM_ERRORS['FAILED_TO_FETCH'];
    }

    if (errorMessage.includes('ERR_CONNECTION_REFUSED') || errorMessage.includes('NetworkError')) {
        return PLATFORM_ERRORS['ERR_CONNECTION_REFUSED'];
    }

    // Handle specific error objects from backend
    if (error?.error && PLATFORM_ERRORS[error.error]) {
        return PLATFORM_ERRORS[error.error];
    }

    // Handle HTTP status codes if provided
    if (error?.status === 401) return PLATFORM_ERRORS['UNAUTHORIZED'];
    if (error?.status === 403) return PLATFORM_ERRORS['FORBIDDEN'];
    if (error?.status === 429) return PLATFORM_ERRORS['RATE_LIMIT_EXCEEDED'];

    return {
        ...PLATFORM_ERRORS['UNKNOWN_ERROR'],
        message: `${PLATFORM_ERRORS['UNKNOWN_ERROR'].message} (Detalhes: ${errorMessage.substring(0, 100)})`
    };
};
